import React from "react";
import imgName from "./Img/coreal.avif";
import photoshopLogo from "./Img/corel4.jpg";
import ReviewsSection from "./ReviewsSection";
import CorelVideo from "./CorelVideo";
import { Link } from "react-router-dom";
import {
  FaLayerGroup,
  FaMagic,
  FaFont,
  FaPaintBrush,
  FaCamera,
  FaPenNib,
  FaVideo,
  FaCube,
  FaRunning,
  FaFileImage,
} from "react-icons/fa";

// Topics
const topics = [
  {
    icon: <FaPaintBrush />,
    title: "Getting Started with CorelDRAW",
    description:
      "Understand the workspace, menus, and basic navigation for efficient designing.",
  },
  {
    icon: <FaCamera />,
    title: "Drawing & Shaping Tools",
    description:
      "Use rectangle, ellipse, polygon, and freehand tools to create precise shapes.",
  },
  {
    icon: <FaLayerGroup />,
    title: "Objects & Layers",
    description:
      "Organize, group, and align objects while managing complex layer structures.",
  },
  {
    icon: <FaFont />,
    title: "Typography & Text Effects",
    description:
      "Design stunning text layouts, apply artistic effects, and manage fonts.",
  },
  {
    icon: <FaMagic />,
    title: "Colors, Fills & Gradients",
    description:
      "Apply solid fills, gradient blends, and patterns to bring designs to life.",
  },
  {
    icon: <FaFileImage />,
    title: "Exporting & File Formats",
    description:
      "Prepare and export designs for print, web, and professional publishing.",
  },
];

// Usage Areas
const usageAreas = [
  {
    icon: <FaPaintBrush />,
    title: "CorelDRAW Basics",
    description:
      "Learn the fundamentals of CorelDRAW, interface navigation, and essential tools for design.",
  },
  {
    icon: <FaPenNib />,
    title: "Vector Illustration",
    description:
      "Create scalable vector artwork, logos, and illustrations with precision drawing tools.",
  },
  {
    icon: <FaVideo />,
    title: "Page Layout & Typography",
    description:
      "Design professional page layouts and master text formatting for brochures and posters.",
  },
  {
    icon: <FaMagic />,
    title: "Advanced Drawing Tools",
    description:
      "Utilize advanced shapes, artistic media, and custom brushes for creative projects.",
  },
  {
    icon: <FaCube />,
    title: "3D & Perspective Drawing",
    description:
      "Add depth to your designs with perspective grids, extrusion tools, and 3D effects.",
  },
  {
    icon: <FaRunning />,
    title: "Exporting & Printing",
    description:
      "Prepare and optimize your designs for print, web, and professional output.",
  },
];

// Future Scope
const futureScope = [
  {
    icon: "🎬",
    title: "Pixar",
    text: "Uses Adobe for animation and post-production.",
  },
  {
    icon: "🎥",
    title: "DreamWorks",
    text: "Integrates Adobe tools in movie workflows.",
  },
  {
    icon: "🌌",
    title: "Industrial Light & Magic (ILM)",
    text: "Relies on Adobe for VFX editing.",
  },
  {
    icon: "🎮",
    title: "Blizzard Entertainment",
    text: "Uses Adobe in game cinematic production.",
  },
  {
    icon: "🕹️",
    title: "Ubisoft",
    text: "Employs Adobe for 3D art and game assets.",
  },
  {
    icon: "⚽",
    title: "Electronic Arts (EA)",
    text: "Leverages Adobe for sports game visuals.",
  },
  {
    icon: "🎭",
    title: "Sony Pictures Animation",
    text: "Uses Adobe in animated film production.",
  },
  {
    icon: "🖥️",
    title: "Weta Digital",
    text: "Incorporates Adobe for post-production work.",
  },
  {
    icon: "💻",
    title: "Autodesk",
    text: "Partners with Adobe for design workflows.",
  },
  {
    icon: "👟",
    title: "Nike",
    text: "Uses Adobe for marketing and branding visuals.",
  },
  {
    icon: "🚗",
    title: "Mercedes-Benz",
    text: "Creates campaigns with Adobe Creative Suite.",
  },
  {
    icon: "🥤",
    title: "Coca-Cola",
    text: "Designs ads and packaging using Adobe.",
  },
  {
    icon: "🔍",
    title: "Google",
    text: "Uses Adobe for creative design and branding.",
  },
  {
    icon: "🍏",
    title: "Apple",
    text: "Employs Adobe tools for promotional visuals.",
  },
  {
    icon: "🏰",
    title: "Disney",
    text: "Creates digital media with Adobe software.",
  },
];

const CorelDRAW = () => {
  return (
    <div className="overflow-hidden">
      {/* HERO SECTION */}
      <section
        className="d-flex align-items-center justify-content-center text-center text-white"
        style={{
          backgroundImage: `url(${imgName})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          height: "400px",
        }}
      >
        {/* You can add heading here if needed */}
      </section>

      {/* TOPICS SECTION */}
      <section className="py-5 container">
        <h1
          className="text-black"
          data-aos="fade-up"
          style={{
            marginBottom: "40px",
            fontSize: "2.5rem",
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          Course Topics
        </h1>

        <div className="row g-4 align-items-stretch">
          {/* Left: Topics */}
          <div
            className="col-lg-6 d-flex flex-column justify-content-center"
            data-aos="fade-right"
          >
            {topics.map((topic, i) => (
              <div
                key={i}
                className="d-flex gap-3 p-3 mb-3 rounded flex-grow-1"
                style={{
                  background: "#f8f8f8",
                  cursor: "pointer",
                  transition: "0.3s",
                }}
                data-aos="fade-up"
                data-aos-delay={i * 100}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-5px)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 20px rgba(77, 208, 225, 0.6)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div
                  style={{ fontSize: "28px", color: "rgba(77, 208, 225, 0.6)" }}
                  className="text-blue-800 animate-pulse"
                >
                  {topic.icon}
                </div>
                <div>
                  <h5 className="fw-bold text-black">{topic.title}</h5>
                  <p className="text-muted small mb-0">{topic.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Right: Image */}
          <div
            className="col-lg-6 d-flex justify-content-center align-items-center"
            data-aos="fade-left"
          >
            <img
              src="/assets/fc10420410ee6901df3c0259b30ebd2c.jpg"
              alt="Adobe Photoshop"
              className="img-fluid rounded shadow"
              style={{
                maxWidth: "450px",
                width: "100%",
                maxHeight: "400px",
                objectFit: "cover",
              }}
            />
          </div>
        </div>
      </section>

      {/* WHERE IT CAN BE USED */}
      <section className="py-5 bg-light">
        <h1
          className="text-black"
          data-aos="fade-up"
          style={{
            marginBottom: "20px",
            fontSize: "2.5rem",
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          Where It Can Be Used
        </h1>
        <div className="container">
          <div className="row g-4 text-black">
            {usageAreas.map((area, i) => {
              const colors = [
                "#ff9800",
                "#4caf50",
                "#2196f3",
                "#e91e63",
                "#9c27b0",
                "#00bcd4",
              ];
              return (
                <div
                  key={i}
                  className="col-sm-6 col-lg-4"
                  data-aos="zoom-in-up"
                  data-aos-delay={i * 100}
                >
                  <div
                    className="p-4 text-center rounded position-relative h-100"
                    style={{
                      background: "#fff",
                      boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
                      animation: "shake 3s infinite ease-in-out",
                    }}
                  >
                    {/* colorful corner */}
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        width: "45px",
                        height: "45px",
                        background: `linear-gradient(135deg, ${
                          colors[i % colors.length]
                        }, transparent)`,
                        borderTopRightRadius: "15px",
                        boxShadow: `0 0 10px ${colors[i % colors.length]}`,
                      }}
                    ></div>
                    <div
                      style={{
                        fontSize: "2rem",
                        color: colors[i % colors.length],
                      }}
                    >
                      {area.icon}
                    </div>
                    <h5 className="fw-bold">{area.title}</h5>
                    <p className="text-muted small">{area.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* VIDEO */}
      <CorelVideo />

      {/* FUTURE SCOPE */}
      <section className="py-5 text-center">
        <h1
          className="text-black"
          data-aos="fade-up"
          style={{
            marginBottom: "20px",
            fontSize: "2.5rem",
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          Future Scope
        </h1>
        <div className="d-flex overflow-hidden text-black" id="my-sc">
          {[0, 1].map((groupIndex) => (
            <div
              key={groupIndex}
              className="d-flex gap-3 flex-shrink-0 scroll-group"
            >
              {futureScope.map((item, i) => {
                const colors = ["#ff5722", "#673ab7", "#3f51b5", "#009688"];
                return (
                  <div
                    key={i}
                    className="p-4 rounded flex-shrink-0 position-relative"
                    style={{
                      background: "linear-gradient(145deg, #fdfdfd, #f3f3f3)",
                      border: "1px solid #eee",
                      minWidth: "250px",
                      animation: "shake 3s infinite ease-in-out",
                      animationDelay: `${i * 500}ms`,
                    }}
                  >
                    {/* colorful corner */}
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        width: "35px",
                        height: "35px",
                        background: `linear-gradient(135deg, ${
                          colors[i % colors.length]
                        }, transparent)`,
                        borderTopRightRadius: "20px",
                        boxShadow: `0 0 10px ${colors[i % colors.length]}`,
                      }}
                    ></div>
                    <div style={{ fontSize: "2rem" }}>{item.icon}</div>
                    <h5 className="fw-bold">{item.title}</h5>
                    <p className="text-muted small">{item.text}</p>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <style>{`
          .scroll-group { animation: marquee 20s linear infinite; }
          @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
          @keyframes shake { 0%,100%{transform:translate(0,0) rotate(0);} 20%{transform:translate(-1px,1px) rotate(-0.5deg);} 40%{transform:translate(1px,-1px) rotate(0.5deg);} 60%{transform:translate(-1px,1px) rotate(-0.5deg);} 80%{transform:translate(1px,-1px) rotate(0.5deg);} }
        `}</style>
      </section>

      {/* Bottom Enroll Section */}
      <section className="max-w-6xl mx-auto px-6 py-12">
        <div
          className="rounded-2xl p-6 bg-gradient-to-r from-rose-600 to-amber-400 text-white shadow-lg flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          data-aos="zoom-in-up"
        >
          <div>
            <h3 className="text-2xl font-bold">Ready to Start Animating?</h3>
            <p className="mt-1 text-sm">
              Join the Adobe Flash Studio course today and create animations,
              banners, and mini-game prototypes that stand out!
            </p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Link
              to="/contact"
              className="px-6 py-3 rounded-lg bg-white text-rose-600 font-semibold shadow hover:opacity-90 transition"
            >
              Enroll Now
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CorelDRAW;
